import * as React from "react";
import { omit } from "lodash";
import { useLocation } from "react-router";
import { useMarketConfig } from "@customer-tools-web/market-extension-resolver";

import { LISTING_STATUS_CODE } from "../const";
import { parseQueryString } from "../../common/parseQueryString";

const DEFAULT_COMMON_VALUE = "All";
const DEFAULT_QUERY_VALUE = "NA";
const DEFAULT_SORT_VALUE = "newest to oldest";

export type SearchParams =
  | "q"
  | "page"
  | "sortBy"
  | "channel"
  | "categoryType"
  | "listingStatus"
  | "propertyType"
  | "qualityLabel"
  | "tier"
  | "unitType"
  | "agency"
  | "branch"
  | "lister"
  | "listingListerType"
  | "toPrice"
  | "fromPrice"
  | "level1"
  | "level2"
  | "level3"
  | "level5"
  | "propertyTypeCodes"
  | "autoUpgrade";

export type QueryParams = { [k in SearchParams]?: string };

type SearchConditions = {
  search_query?: string;
  search_page_number?: string;
  search_sort_by?: string;
  search_filters_channel?: string;
  search_filters_property_type?: string;
  search_filters_upgrade_automation?: string;
  search_filters_tier?: string;
  search_filters_unit_type?: string;
  search_filters_pricerange_min?: string;
  search_filters_pricerange_max?: string;
  search_filters_quality?: string;
  search_filters_agency?: string;
  search_filters_branch?: string;
  search_filters_agent?: string;
  search_filters_agent_type?: string;
  search_location_level_1?: string;
  search_location_level_2?: string;
  search_location_level_3?: string;
  search_listing_status?: string;
  search_listing_layout?: string;
};

export type SearchQueryProps = {
  buildSearchConditions: () => SearchConditions;
  setQueryParams: (queryParams: QueryParams) => void;
};

const queryParamsRef: { params: QueryParams } = { params: {} };

export function useSearchQuery() {
  const location = useLocation();
  const config = useMarketConfig();

  function setQueryParams(queryParams: QueryParams) {
    queryParamsRef.params = {
      ...queryParamsRef.params,
      ...queryParams,
    };
  }

  function generateReferenceQuery() {
    const fieldsWithLabel = [
      "listingStatus",
      "propertyType",
      "tier",
      "channel",
      "categoryType",
      "agency",
      "branch",
      "lister",
      "level1",
      "level2",
      "level3",
      "level5",
      "toPrice",
      "fromPrice",
      "autoUpgrade",
      "sortBy",
      "unitType",
    ];
    return omit(parseQueryString(location.search), fieldsWithLabel);
  }

  function getAutoUpgradeLabel(autoUpgrade: string) {
    if (autoUpgrade === "1") return "On";
    if (autoUpgrade === "2") return "Off";
    return "All";
  }

  function getMyExtend(query: any) {
    const myExtend = {
      search_location_level_5: query.level5 || DEFAULT_COMMON_VALUE,
      search_filters_category_type: query.categoryType || DEFAULT_COMMON_VALUE,
      search_filters_unit_type: query.unitType || DEFAULT_COMMON_VALUE,
      search_filters_pricerange_min: query.fromPrice || "0",
      search_filters_pricerange_max: query.toPrice || "0",
    };

    if (config.market === "MY") return myExtend;
    return {};
  }

  function buildSearchConditions(): SearchConditions {
    const query = {
      ...queryParamsRef.params,
      ...generateReferenceQuery(),
    } as any;

    const autoUpgradeLabel = getAutoUpgradeLabel(query.autoUpgrade);
    const myExtend = getMyExtend(query);

    return {
      ...myExtend,
      search_query: query.q || DEFAULT_QUERY_VALUE,
      search_page_number: query.page || "1",
      search_sort_by: query.sortBy || DEFAULT_SORT_VALUE,
      search_filters_channel: query.channel || DEFAULT_COMMON_VALUE,
      search_filters_property_type: query.propertyType || DEFAULT_COMMON_VALUE,
      search_filters_upgrade_automation:
        autoUpgradeLabel || DEFAULT_COMMON_VALUE,
      search_filters_tier: query.tier || DEFAULT_COMMON_VALUE,
      search_location_level_1: query.level1 || DEFAULT_COMMON_VALUE,
      search_location_level_2: query.level2 || DEFAULT_COMMON_VALUE,
      search_filters_agency: query.agency || DEFAULT_COMMON_VALUE,
      search_filters_branch: query.branch || DEFAULT_COMMON_VALUE,
      search_filters_agent: query.lister || DEFAULT_COMMON_VALUE,
      search_filters_agent_type:
        query.listingListerType || DEFAULT_COMMON_VALUE,
      search_listing_status:
        query.listingStatus || `${LISTING_STATUS_CODE.ONLINE}`,
    };
  }

  return {
    buildSearchConditions,
    setQueryParams,
  };
}

export function withSearchQuery<
  Props extends SearchQueryProps = SearchQueryProps
>(
  Component: React.ComponentType<Props>
): React.ComponentType<Omit<Props, keyof SearchQueryProps>> {
  return function Wrapper(props: Omit<Props, keyof SearchQueryProps>) {
    const { buildSearchConditions, setQueryParams } = useSearchQuery();
    return (
      <Component
        {...(props as any)}
        buildSearchConditions={buildSearchConditions}
        setQueryParams={setQueryParams}
      />
    );
  };
}
